import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  LoginRequest,
  PublicUser,
  RegisterRequest,
  UpdateSettingsRequest,
  UserSettings,
} from '@firegame/shared';
import * as authApi from '../api/auth.js';

/**
 * Etat d'authentification partage par toute l'application.
 *
 * La session vit dans un cookie `httpOnly` que le JavaScript ne peut pas lire :
 * l'etat local est donc reconstruit au chargement par un appel a `/auth/me`.
 * C'est la seule source de verite, ce qui evite toute divergence entre ce que
 * croit le navigateur et ce que sait le serveur.
 */
interface AuthContextValue {
  user: PublicUser | null;
  settings: UserSettings | null;
  /** Vrai tant que la session initiale n'a pas ete verifiee. */
  initializing: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (input: LoginRequest) => Promise<void>;
  register: (input: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  updateSettings: (input: UpdateSettingsRequest) => Promise<void>;
  /** Force une relecture de la session (apres changement de mot de passe...). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    authApi
      .fetchSession(controller.signal)
      .then((session) => {
        if (!active) return;
        setUser(session.user);
        setSettings(session.settings);
      })
      .catch(() => {
        // Serveur injoignable : on reste sur un etat anonyme, l'interface
        // affichera l'erreur au moment d'une action necessitant le reseau.
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setInitializing(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const login = useCallback(async (input: LoginRequest) => {
    const session = await authApi.login(input);
    setUser(session.user);
    setSettings(session.settings);
  }, []);

  const register = useCallback(async (input: RegisterRequest) => {
    const session = await authApi.register(input);
    setUser(session.user);
    setSettings(session.settings);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
    setSettings(null);
  }, []);

  const updateSettings = useCallback(async (input: UpdateSettingsRequest) => {
    const session = await authApi.updateSettings(input);
    setUser(session.user);
    setSettings(session.settings);
  }, []);

  const refresh = useCallback(async () => {
    const session = await authApi.fetchSession();
    setUser(session.user);
    setSettings(session.settings);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      settings,
      initializing,
      isAuthenticated: user !== null,
      isAdmin: user?.role === 'ADMIN',
      login,
      register,
      logout,
      updateSettings,
      refresh,
    }),
    [user, settings, initializing, login, register, logout, updateSettings, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit etre utilise a l'interieur d'un AuthProvider.");
  }
  return context;
}
